import { DocumentTypes } from '@introspection/documentTypes';
import { IMutation, IQuery, ISessionType, IStartVsCodePayload } from '@introspection/index';
import { Reader } from '@rhtml/di';
import { Inject, Injectable } from '@rxdi/core';
import { DataProxy, deepCopy, importQuery } from '@rxdi/graphql-client';
import { DocumentNode } from 'graphql';
import { firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { BaseGraphqlService } from '../base';
import { modCard } from '../card/card.helpers';
import { CardService } from '../card/card.service';
import { GithubService } from '../github/github.service';

@Injectable()
export class VSCodeService extends BaseGraphqlService {
  @Inject(CardService)
  private cardService: CardService;
  @Inject(GithubService)
  private githubservice: GithubService;

  private _getQuery(): DocumentNode {
    return importQuery<DocumentTypes>('get-project.query.graphql');
  }

  private _getVaribales(id: string) {
    return {
      id,
    };
  }

  private _getState = (proxy: DataProxy) => (projectId: string) =>
    deepCopy(
      proxy.readQuery<IQuery>({
        query: this._getQuery(),
        variables: this._getVaribales(projectId),
      })
    );

  private _writeState = (data: IQuery) => (proxy: DataProxy) => (projectId: string) =>
    proxy.writeQuery<IQuery>({
      query: this._getQuery(),
      data,
      variables: this._getVaribales(projectId),
    });

  stopSession(session: ISessionType): Reader<ISessionType[], Observable<ISessionType>> {
    return (sessions) =>
      this.mutate({
        mutation: 'stop-session.graphql',
        variables: {
          id: session.id,
        },
        update: (proxy) => {
          const data = this._getState(proxy)(session.projectId);
          const cardState = this._getCardState(session.cardId)(proxy);
          cardState.getCard.sessions[0].active = false;
          this._writeCardState(cardState, { id: session.cardId })(proxy);
          this._writeState({
            getProject: {
              ...data.getProject,
              sessions: [...sessions.filter((s) => s.id !== session.id), { ...session, active: false }],
            },
          })(proxy);
        },
      }).pipe(map(({ data }) => data.stopSession));
  }

  generateSession(args: {
    name: string;
    machineLabel: string;
    password: string;
    cardId?: string;
    ports?: string[];
  }): Reader<string, Observable<ISessionType>> {
    return (projectId) =>
      this.mutate({
        mutation: 'generate-code-session.graphql',
        variables: {
          projectId,
          ...args,
        },
        update: async (proxy, { data: { generateCodeSession } }: { data: IMutation }) => {
          const data = this._getState(proxy)(projectId);
          if (args.cardId) {
            const card = await firstValueFrom(this.cardService.getCard(args.cardId));
            data.getBoard = modCard(
              card.boardId,
              card.columnId,
              card.id
            )((card) => {
              card.sessions.push(generateCodeSession);
              return card;
            })(data.getBoard);
          }
          try {
            this._writeState({
              getBoard: data.getBoard,
            })(proxy);
          } catch (e) {
            // console.error(e);
          }
        },
      }).pipe(map((res) => res.data.generateCodeSession));
  }

  deleteSession(session: ISessionType): Reader<ISessionType[], Observable<ISessionType>> {
    return () =>
      this.mutate({
        mutation: 'vs-code-delete-session.graphql',
        variables: {
          id: session.id,
        },
        update: (proxy) => {
          const data = this._getState(proxy)(session.projectId);
          if (session.card) {
            data.getBoard = modCard(
              session.card.boardId,
              session.card.columnId,
              session.card.id
            )((card) => {
              card.sessions = card.sessions.filter((s) => s.id !== session.id);
              return card;
            })(data.getBoard);
          }
          this._writeState({
            getBoard: data.getBoard,
          })(proxy);
        },
      }).pipe(map(({ data }) => data.deleteSession));
  }

  startSession({
    ports,
    session,
    repoName,
    owner,
  }: {
    session: ISessionType;
    repoName: string;
    ports?: string[];
    owner: string;
  }): Reader<ISessionType[], Observable<ISessionType>> {
    if (!session) {
      throw new Error('Missing session');
    }
    const token = this.githubservice.getAccessToken();
    if (!token) {
      throw new Error('Missing Github token please try to logout and login again');
    }
    return () =>
      this.mutate({
        mutation: 'start-session.graphql',
        variables: {
          id: session.id,
          ports: ports || [],
          repoName,
          token,
          owner,
        },
        update: (proxy) => {
          const data = this._getState(proxy)(session.projectId);
          const cardState = this._getCardState(session.cardId)(proxy);
          cardState.getCard.sessions[0].active = true;
          this._writeCardState(cardState, { id: session.cardId })(proxy);
          this._writeState({
            ...cardState,
            getProject: {
              ...data.getProject,
              sessions: [
                ...data.getProject.sessions.filter((s) => s.id !== session.id),
                {
                  ...session,
                  active: true,
                },
              ],
            },
          })(proxy);
        },
      }).pipe(map(({ data }) => data.startSession));
  }

  startVsCodeEvent(label: string, args: IStartVsCodePayload) {
    return this.mutate({
      mutation: 'start-vs-code-event.graphql',
      variables: {
        label,
        args,
      },
    });
  }

  stopVsCodeEvent(label: string, specifier: string) {
    return this.mutate({
      mutation: 'stop-vs-code-event.graphql',
      variables: {
        label,
        args: { specifier },
      },
    });
  }

  _writeCardState(data: IQuery, variables: { id: string }) {
    return (proxy: DataProxy) =>
      proxy.writeQuery<IQuery>({
        query: importQuery<DocumentTypes>('get-card.graphql'),
        data,
        variables,
      });
  }

  _getCardState(id: string) {
    return (proxy: DataProxy) =>
      deepCopy(
        proxy.readQuery({
          query: importQuery<DocumentTypes>('get-card.graphql'),
          variables: {
            id,
          },
        })
      );
  }
}
